import { EngineContext, Vec2 } from '../../engine';
import { EnvironmentBackdrop, EnvironmentType } from '../environment';
import { Fighter } from '../fighter';
import { ARENA, GAME_CONFIG, SCENES, UI } from '../constants';
import { Scene, SceneManager } from './sceneManager';
import { drawCenteredText, drawText } from '../../ui/text';
import { drawScroll, centerScroll } from '../../ui/scroll';
import { GameServices } from '../services';
import { WombatAction, WombatDirection } from '../../assets/wombatSprites';
import { WOMBAT_FACTS, shuffleFacts } from '../facts';
import type { SuperSoundVariant } from '../../audio/audioManager';
import type { RankingEntry } from '../ranking/rankingService';

interface CombatPayload {
  level: number;
  superMeter?: number;
  superCooldownRemaining?: number;
  resetSuper?: boolean;
  score?: number;
  playerName?: string;
}

type CombatState = 'intro' | 'fight' | 'victory' | 'defeat';

type SuperTrailEmitter = (
  position: Vec2,
  direction: Vec2,
  particles: EngineContext['particles'],
) => void;

type SuperImpactEmitter = (position: Vec2, particles: EngineContext['particles']) => void;

interface SuperAttackVariant {
  id: SuperSoundVariant;
  travelTime: number;
  damageMultiplier: number;
  knockback: number;
  screenShake: number;
  projectileRadius: number;
  coreColor: string;
  auraColor: string;
  trailEmitter: SuperTrailEmitter;
  impactEmitter: SuperImpactEmitter;
}

interface ActiveSuperAttack {
  variant: SuperAttackVariant;
  direction: Vec2;
  duration: number;
  elapsed: number;
  position: Vec2;
  hasHit: boolean;
  targetEntity: Fighter;
  speed: number;
}

const randomRange = (min: number, max: number): number => min + Math.random() * (max - min);

const perpendicular = (direction: Vec2): Vec2 => new Vec2(-direction.y, direction.x).normalize();

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

const SUPER_ATTACK_VARIANTS: SuperAttackVariant[] = [
  {
    id: 'solar',
    travelTime: 0.58,
    damageMultiplier: 3.2,
    knockback: 320,
    screenShake: 0.82,
    projectileRadius: 10,
    coreColor: '#fff2a6',
    auraColor: '#ffbc4b',
    trailEmitter: (position, direction, particles) => {
      const colors = ['#ffce6b', '#ffae45', '#fffbca'];
      const normal = perpendicular(direction);
      for (let i = 0; i < 5; i += 1) {
        const spawn = position
          .clone()
          .add(direction.clone().scale(-randomRange(6, 18)))
          .add(normal.clone().scale((Math.random() - 0.5) * 14));
        particles.emit({
          position: spawn,
          velocity: new Vec2((Math.random() - 0.5) * 55, (Math.random() - 0.2) * 60 - 20),
          lifetime: randomRange(0.24, 0.4),
          color: colors[Math.floor(Math.random() * colors.length)],
          size: 3,
        });
      }
    },
    impactEmitter: (position, particles) => {
      const colors = ['#fff7c6', '#ffde7a', '#ff9548'];
      for (let i = 0; i < 50; i += 1) {
        const angle = Math.random() * Math.PI * 2;
        const speed = randomRange(140, 260);
        particles.emit({
          position: position.clone(),
          velocity: Vec2.fromAngle(angle, speed),
          lifetime: randomRange(0.32, 0.6),
          color: colors[i % colors.length],
          size: 3,
        });
      }
    },
  },
  {
    id: 'frost',
    travelTime: 0.64,
    damageMultiplier: 2.9,
    knockback: 300,
    screenShake: 0.74,
    projectileRadius: 9,
    coreColor: '#c7f4ff',
    auraColor: '#7ad9ff',
    trailEmitter: (position, direction, particles) => {
      const colors = ['#bff3ff', '#89e0ff', '#e2fbff'];
      const normal = perpendicular(direction);
      for (let i = 0; i < 6; i += 1) {
        const swirl = direction
          .clone()
          .scale(-randomRange(12, 24));
        const offset = normal.clone().scale((Math.random() - 0.5) * 20);
        const spawn = position.clone().add(swirl).add(offset);
        particles.emit({
          position: spawn,
          velocity: new Vec2((Math.random() - 0.5) * 40, -randomRange(10, 40)),
          lifetime: randomRange(0.36, 0.52),
          color: colors[Math.floor(Math.random() * colors.length)],
          size: 3,
        });
      }
    },
    impactEmitter: (position, particles) => {
      const colors = ['#e1f9ff', '#a9e6ff', '#79d2ff'];
      for (let ring = 0; ring < 3; ring += 1) {
        const radius = 18 + ring * 12;
        const particlesPerRing = 28;
        for (let i = 0; i < particlesPerRing; i += 1) {
          const angle = (i / particlesPerRing) * Math.PI * 2;
          const speed = randomRange(90, 180);
          particles.emit({
            position: position
              .clone()
              .add(Vec2.fromAngle(angle, radius)),
            velocity: Vec2.fromAngle(angle, speed),
            lifetime: randomRange(0.38, 0.58),
            color: colors[(i + ring) % colors.length],
            size: 3,
          });
        }
      }
    },
  },
  {
    id: 'thunder',
    travelTime: 0.48,
    damageMultiplier: 3.05,
    knockback: 340,
    screenShake: 0.88,
    projectileRadius: 11,
    coreColor: '#f5f8ff',
    auraColor: '#b7c4ff',
    trailEmitter: (position, direction, particles) => {
      const colors = ['#dfe7ff', '#bdc7ff', '#f9fbff'];
      const normal = perpendicular(direction);
      for (let i = 0; i < 4; i += 1) {
        const jitter = direction
          .clone()
          .scale(-randomRange(4, 14));
        const zigzag = normal.clone().scale((Math.random() - 0.5) * 28);
        const spawn = position.clone().add(jitter).add(zigzag);
        const angle = Math.atan2(direction.y, direction.x) + (Math.random() - 0.5) * 0.6;
        const speed = randomRange(160, 260);
        particles.emit({
          position: spawn,
          velocity: Vec2.fromAngle(angle, speed),
          lifetime: randomRange(0.22, 0.36),
          color: colors[Math.floor(Math.random() * colors.length)],
          size: 3,
        });
      }
    },
    impactEmitter: (position, particles) => {
      const colors = ['#f7faff', '#d4ddff', '#9aaeff', '#fdfcff'];
      for (let burst = 0; burst < 4; burst += 1) {
        const angleBase = Math.random() * Math.PI * 2;
        for (let i = 0; i < 12; i += 1) {
          const angle = angleBase + (i / 12) * Math.PI * 2;
          const speed = randomRange(210, 320);
          particles.emit({
            position: position.clone(),
            velocity: Vec2.fromAngle(angle, speed),
            lifetime: randomRange(0.28, 0.46),
            color: colors[i % colors.length],
            size: 3,
          });
        }
      }
    },
  },
  {
    id: 'ember',
    travelTime: 0.62,
    damageMultiplier: 3,
    knockback: 310,
    screenShake: 0.8,
    projectileRadius: 10,
    coreColor: '#ffdb8c',
    auraColor: '#ff8358',
    trailEmitter: (position, direction, particles) => {
      const colors = ['#ffb36b', '#ff8f47', '#ffdba3'];
      const normal = perpendicular(direction);
      for (let i = 0; i < 7; i += 1) {
        const backwards = direction
          .clone()
          .scale(-randomRange(10, 22));
        const offset = normal.clone().scale((Math.random() - 0.5) * 18);
        const spawn = position.clone().add(backwards).add(offset);
        particles.emit({
          position: spawn,
          velocity: new Vec2((Math.random() - 0.5) * 90, -randomRange(20, 80)),
          lifetime: randomRange(0.28, 0.5),
          color: colors[Math.floor(Math.random() * colors.length)],
          size: 3,
        });
      }
    },
    impactEmitter: (position, particles) => {
      const colors = ['#ffde9b', '#ff9f58', '#ff7a45'];
      const bursts = 5 + Math.floor(Math.random() * 3);
      for (let burst = 0; burst < bursts; burst += 1) {
        const angleBase = Math.random() * Math.PI * 2;
        for (let i = 0; i < 16; i += 1) {
          const angle = angleBase + (i / 16) * Math.PI * 2;
          const speed = randomRange(130, 250);
          particles.emit({
            position: position.clone(),
            velocity: Vec2.fromAngle(angle, speed),
            lifetime: randomRange(0.32, 0.56),
            color: colors[(i + burst) % colors.length],
            size: 3,
          });
        }
      }
    },
  },
  {
    id: 'void',
    travelTime: 0.7,
    damageMultiplier: 3.25,
    knockback: 330,
    screenShake: 0.86,
    projectileRadius: 12,
    coreColor: '#d7c6ff',
    auraColor: '#7b5dff',
    trailEmitter: (position, direction, particles) => {
      const colors = ['#c9b9ff', '#8b77ff', '#e9e1ff'];
      const normal = perpendicular(direction);
      for (let i = 0; i < 5; i += 1) {
        const swirl = direction.clone().scale(-randomRange(14, 26));
        const offset = normal.clone().scale((Math.random() - 0.5) * 22);
        const spin = position.clone().add(swirl).add(offset);
        const theta = Math.atan2(direction.y, direction.x) + (Math.random() - 0.5) * Math.PI;
        const speed = randomRange(100, 200);
        particles.emit({
          position: spin,
          velocity: Vec2.fromAngle(theta, speed),
          lifetime: randomRange(0.34, 0.54),
          color: colors[Math.floor(Math.random() * colors.length)],
          size: 3,
        });
      }
    },
    impactEmitter: (position, particles) => {
      const colors = ['#f0e9ff', '#c5b7ff', '#8f73ff', '#d9cbff'];
      for (let arm = 0; arm < 3; arm += 1) {
        const angleOffset = (arm / 3) * Math.PI * 2;
        for (let i = 0; i < 24; i += 1) {
          const angle = angleOffset + Math.sin(i * 0.3) * 0.6 + (i / 24) * Math.PI * 2;
          const speed = randomRange(160, 280);
          particles.emit({
            position: position.clone(),
            velocity: Vec2.fromAngle(angle, speed),
            lifetime: randomRange(0.38, 0.62),
            color: colors[(i + arm) % colors.length],
            size: 3,
          });
        }
      }
    },
  },
];

interface LevelReward {
  playerSpeed: number;
  playerDamage: number;
  playerHealth: number;
  enemySpeed: number;
  enemyDamage: number;
  enemyHealth: number;
}

const PLAYER_BASE_STATS = {
  maxHealth: 148,
  speed: GAME_CONFIG.basePlayerSpeed,
  strength: GAME_CONFIG.basePlayerDamage,
};

const ENEMY_BASE_STATS = {
  maxHealth: 138,
  speed: GAME_CONFIG.baseEnemySpeed,
  strength: GAME_CONFIG.baseEnemyDamage,
};

const OPPONENT_NAMES = [
  'Mason',
  'Harper',
  'Logan',
  'Avery',
  'Carter',
  'Sloane',
  'Parker',
  'Reese',
  'Hudson',
  'Quinn',
];

export class CombatScene implements Scene {
  key = SCENES.COMBAT;
  private manager: SceneManager;
  private services: GameServices;
  private playerStats = { ...PLAYER_BASE_STATS };
  private enemyStats = { ...ENEMY_BASE_STATS };
  private player = new Fighter(
    new Vec2(ARENA.centerX - 60, ARENA.groundY - 32),
    this.playerStats,
  );
  private enemy = new Fighter(
    new Vec2(ARENA.centerX + 36, ARENA.groundY - 32),
    this.enemyStats,
  );
  private environmentType: EnvironmentType = 'forest';
  private backdrop = new EnvironmentBackdrop(Date.now(), 'forest');
  private state: CombatState = 'intro';
  private stateTimer = 0;
  private level = 1;
  private enemyName = 'Mason';
  private message = '';
  private enemyAttackCooldown = 0;
  private screenShake = 0;
  private readonly superCooldown = 7;
  private readonly superReadyFlashDuration = 2;
  private superMeter = 1;
  private superReadyTimer = 0;
  private superReadyAnnounced = false;
  private superCooldownRemaining = 0;
  private activeSuperAttacks: ActiveSuperAttack[] = [];
  private readonly firstFact = WOMBAT_FACTS[0];
  private factDeck: string[] = shuffleFacts(WOMBAT_FACTS.slice(1));
  private factCursor = 0;
  private currentFact = this.firstFact;
  private factCardBounds = { x: 0, y: 0, width: 0, height: 0 };
  private hitStreak = 0;
  private dopaminePopups: Array<{
    text: string;
    timer: number;
    duration: number;
    x: number;
    y: number;
    color: string;
  }> = [];
  private playerScaling = { speed: 1, damage: 1, health: 1 };
  private enemyScaling = { speed: 1, damage: 1, health: 1 };
  private nextReward: LevelReward = this.createReward();
  private score = 0;
  private playerNickname = 'Anonymous';
  private rankingEntries: RankingEntry[] = [];
  private playerPlacement?: number;
  private scoreSubmissionMessage = '';
  private scoreSubmissionStarted = false;
  private defeatBoardTimer = 0;
  private defeatBoardVisible = false;

  constructor(manager: SceneManager, services: GameServices) {
    this.manager = manager;
    this.services = services;
  }

  init(): void {
    this.state = 'intro';
    this.stateTimer = 0;
    this.message = '';
    this.superReadyTimer = 0;
    this.superReadyAnnounced = this.superMeter >= 1;
    this.activeSuperAttacks = [];
    this.resetFactDeck();
    this.currentFact = this.firstFact;
    this.factCardBounds = { x: 0, y: 0, width: 0, height: 0 };
    this.score = 0;
    this.playerNickname = this.services.ranking.getNickname();
    this.rankingEntries = this.services.ranking.getEntries();
    this.playerPlacement = undefined;
    this.scoreSubmissionMessage = '';
    this.scoreSubmissionStarted = false;
    this.defeatBoardTimer = 0;
    this.defeatBoardVisible = false;
  }

  onEnter(context: EngineContext, payload?: CombatPayload): void {
    this.level = payload?.level ?? 1;
    this.configureLevel();
    this.environmentType = this.environmentForLevel(this.level);
    this.backdrop = new EnvironmentBackdrop(Date.now() + this.level * 1337, this.environmentType);
    this.player.reset(new Vec2(ARENA.centerX - 58, ARENA.groundY - this.player.height), this.playerStats);
    this.enemy.reset(new Vec2(ARENA.centerX + 38, ARENA.groundY - this.enemy.height), this.enemyStats);
    this.enemyName = OPPONENT_NAMES[(this.level * 137) % OPPONENT_NAMES.length];
    this.services.audio.playBackground(this.environmentType);
    this.screenShake = 0;
    this.applySuperCarryover(payload);
    this.activeSuperAttacks = [];
    this.currentFact = this.selectFactForLevel(this.level);
    this.factCardBounds = { x: 0, y: 0, width: 0, height: 0 };
    this.score = payload?.score ?? 0;
    this.playerNickname = payload?.playerName ?? this.services.ranking.getNickname();
    this.rankingEntries = this.services.ranking.getEntries();
    this.playerPlacement = undefined;
    this.scoreSubmissionMessage = '';
    this.scoreSubmissionStarted = false;
    this.defeatBoardTimer = 0;
    this.defeatBoardVisible = false;
    this.scheduleIntro();
    void this.services.ranking
      .refreshEntries()
      .then((entries) => {
        this.rankingEntries = entries;
      })
      .catch(() => undefined);
  }

  update(context: EngineContext): void {
    const dt = context.delta;
    this.screenShake = Math.max(0, this.screenShake - dt * 2.1);
    this.stateTimer = Math.max(0, this.stateTimer - dt);

    switch (this.state) {
      case 'intro':
        if (this.stateTimer <= 0) {
          this.state = 'fight';
          this.message = '';
          this.beginFight();
        }
        break;
      case 'fight':
        this.handlePlayerInput(context);
        this.handleEnemyAI(dt);
        this.player.update(dt);
        this.enemy.update(dt);
        this.updatePopups(dt);
        this.updateSuperMeter(dt);
        this.updateSuperAttacks(context, dt);
        this.resolveCombat(context);
        this.checkOutcome(context);
        break;
      case 'victory':
        if (this.stateTimer <= 0 && this.confirmPressed(context)) {
          this.manager.transition(SCENES.COMBAT, {
            level: this.level + 1,
            superMeter: this.superMeter,
            superCooldownRemaining: this.superCooldownRemaining,
            score: this.score,
            playerName: this.playerNickname,
          });
        }
        break;
      case 'defeat':
        if (!this.defeatBoardVisible) {
          this.defeatBoardTimer += dt;
          if (this.defeatBoardTimer >= 1) {
            this.defeatBoardVisible = true;
          }
        }
        if (this.stateTimer <= 0 && this.confirmPressed(context)) {
          this.manager.transition(SCENES.MENU);
        }
        break;
      default:
        break;
    }
  }

  render(context: EngineContext): void {
    const { ctx, time } = context;
    ctx.fillStyle = '#050b08';
    ctx.fillRect(0, 0, ARENA.width, ARENA.height);

    ctx.save();
    if (this.screenShake > 0) {
      const intensity = this.screenShake;
      const offsetX = (Math.random() - 0.5) * 8 * intensity;
      const offsetY = (Math.random() - 0.5) * 6 * intensity;
      ctx.translate(offsetX, offsetY);
    }
    this.backdrop.render(ctx, time);
    this.drawGround(ctx);
    this.drawFighters(ctx, time);
    this.renderSuperAttacks(ctx);
    ctx.restore();

    this.drawHUD(ctx);
    this.drawSuperMeter(ctx);
    this.drawPopups(ctx);

    if (this.state === 'intro' && this.stateTimer > 0) {
      this.drawFactCard(ctx);
      const countdown = Math.ceil(Math.max(this.stateTimer, 0));
      const labelY = this.factCardBounds.y - 14;
      if (labelY > 16) {
        drawText(ctx, this.message, ARENA.centerX, labelY - 10, 'center', UI.font, UI.colorAccent);
        drawText(ctx, `Start in ${countdown}`, ARENA.centerX, labelY, 'center', UI.font, UI.colorPrimary);
      } else {
        drawCenteredText(ctx, `Start in ${countdown}`, 32, UI.font, UI.colorPrimary);
      }
    }

    this.drawFooter(ctx);

    if (this.state === 'victory') {
      const victoryColors = this.victoryPalette();
      drawCenteredText(ctx, 'Victory!', 72, UI.largeFont, victoryColors.headline);
      drawCenteredText(ctx, 'Press confirm to continue', 110, UI.font, victoryColors.body);
    }

    if (this.state === 'defeat') {
      this.drawDefeatSummary(ctx);
      if (!this.defeatBoardVisible) {
        drawCenteredText(ctx, 'Defeat!', 70, UI.largeFont, UI.colorWarning);
        drawCenteredText(ctx, 'Press confirm for main menu', 120, UI.font, UI.colorPrimary);
      }
    }
  }

  private handlePlayerInput(context: EngineContext): void {
    const { input, delta } = context;
    const left = input.isHeld('a') || input.isHeld('arrowleft');
    const right = input.isHeld('d') || input.isHeld('arrowright');
    const jump = input.isPressed('w') || input.isPressed('arrowup');
    const punch = input.isPressed('p');
    const kick = input.isPressed('k');
    const superBlast = input.isPressed(' ');

    const direction = Number(right) - Number(left);
    this.player.move(direction, delta);

    if (jump) {
      this.player.jump();
    }

    if (superBlast) {
      this.attemptSuperAttack(context);
    }

    if (punch) {
      this.player.tryAttack('punch');
    }

    if (kick) {
      this.player.tryAttack('kick');
    }
  }

  private attemptSuperAttack(context: EngineContext): void {
    if (this.state !== 'fight') {
      return;
    }
    if (this.superMeter < 1) {
      return;
    }
    if (!this.enemy.isAlive()) {
      return;
    }
    this.superMeter = 0;
    this.superCooldownRemaining = this.superCooldown;
    this.superReadyAnnounced = false;
    this.superReadyTimer = 0;
    this.launchSuperAttack(context);
  }

  private addScore(points: number): void {
    const sanitized = Math.max(0, Math.round(points));
    this.score = Math.max(0, this.score + sanitized);
  }

  private launchSuperAttack(context: EngineContext): void {
    const variant = SUPER_ATTACK_VARIANTS[Math.floor(Math.random() * SUPER_ATTACK_VARIANTS.length)];
    const origin = this.getPlayerSuperOrigin();
    const target = this.getSuperTargetForFighter(this.enemy);
    const travel = Vec2.sub(target, origin);
    const direction = travel.clone().normalize();
    const distance = travel.length();
    const speed = Math.max(distance / variant.travelTime, 280);
    const instance: ActiveSuperAttack = {
      variant,
      direction,
      duration: variant.travelTime,
      elapsed: 0,
      position: origin.clone(),
      hasHit: false,
      targetEntity: this.enemy,
      speed,
    };
    this.activeSuperAttacks.push(instance);
    variant.trailEmitter(origin, direction, context.particles);
    this.services.audio.playSuperAttack(variant.id);
  }

  private getPlayerSuperOrigin(): Vec2 {
    return new Vec2(
      this.player.position.x + this.player.width / 2 + this.player.facing * 14,
      this.player.position.y + this.player.height / 2 - 6,
    );
  }

  private getSuperTargetForFighter(fighter: Fighter): Vec2 {
    return new Vec2(
      fighter.position.x + fighter.width / 2,
      fighter.position.y + fighter.height / 2 - 8,
    );
  }

  private applySuperCarryover(payload?: CombatPayload): void {
    if (payload?.resetSuper) {
      this.superMeter = 1;
      this.superCooldownRemaining = 0;
    } else if (payload?.superMeter !== undefined || payload?.superCooldownRemaining !== undefined) {
      const providedMeter = payload.superMeter ?? (payload.superCooldownRemaining !== undefined
        ? 1 - payload.superCooldownRemaining / this.superCooldown
        : this.superMeter);
      this.superMeter = clamp(providedMeter, 0, 1);
      if (payload.superCooldownRemaining !== undefined) {
        this.superCooldownRemaining = clamp(payload.superCooldownRemaining, 0, this.superCooldown);
      } else {
        this.superCooldownRemaining = this.superCooldown * (1 - this.superMeter);
      }
    } else if (this.superCooldownRemaining > 0) {
      this.superMeter = clamp(1 - this.superCooldownRemaining / this.superCooldown, 0, 1);
    } else {
      this.superMeter = clamp(this.superMeter, 0, 1);
      this.superCooldownRemaining = 0;
    }

    if (this.superMeter >= 1) {
      this.superReadyAnnounced = true;
      this.superReadyTimer = this.superReadyFlashDuration;
      this.superCooldownRemaining = 0;
    } else {
      this.superReadyAnnounced = false;
      this.superReadyTimer = 0;
    }
  }

  private resetFactDeck(): void {
    this.factDeck = shuffleFacts(WOMBAT_FACTS.slice(1));
    this.factCursor = 0;
  }

  private selectFactForLevel(level: number): string {
    if (level <= 1) {
      this.resetFactDeck();
      return this.firstFact;
    }
    return this.nextFactFromDeck();
  }

  private nextFactFromDeck(): string {
    if (this.factDeck.length === 0) {
      this.resetFactDeck();
    }
    if (this.factCursor >= this.factDeck.length) {
      this.factDeck = shuffleFacts(this.factDeck);
      this.factCursor = 0;
    }
    const fact = this.factDeck[this.factCursor];
    this.factCursor += 1;
    return fact;
  }

  private updateSuperMeter(dt: number): void {
    if (this.superMeter >= 1) {
      this.superMeter = 1;
      this.superCooldownRemaining = 0;
      if (!this.superReadyAnnounced) {
        this.superReadyAnnounced = true;
        this.superReadyTimer = this.superReadyFlashDuration;
      }
    } else {
      this.superMeter = Math.min(1, this.superMeter + dt / this.superCooldown);
      this.superCooldownRemaining = Math.max(0, this.superCooldown * (1 - this.superMeter));
      this.superReadyAnnounced = false;
      this.superReadyTimer = 0;
      if (this.superMeter >= 1) {
        this.superReadyAnnounced = true;
        this.superReadyTimer = this.superReadyFlashDuration;
      }
    }
    if (this.superReadyTimer > 0) {
      this.superReadyTimer = Math.max(0, this.superReadyTimer - dt);
    }
  }

  private updateSuperAttacks(context: EngineContext, dt: number): void {
    if (!this.activeSuperAttacks.length) {
      return;
    }
    const { particles } = context;
    this.activeSuperAttacks = this.activeSuperAttacks.filter((attack) => {
      attack.elapsed += dt;
      const targetPosition = this.getSuperTargetForFighter(attack.targetEntity);
      const toTarget = Vec2.sub(targetPosition, attack.position);
      const distanceToTarget = toTarget.length();
      const travelStep = attack.speed * dt;
      if (distanceToTarget > 0 && travelStep > 0) {
        const direction = toTarget.clone().scale(1 / distanceToTarget);
        const moveDistance = Math.min(distanceToTarget, travelStep);
        attack.position.add(direction.clone().scale(moveDistance));
        attack.direction = direction;
      } else if (distanceToTarget === 0) {
        attack.position = targetPosition.clone();
      }
      const renderPosition = attack.position.clone();
      attack.variant.trailEmitter(renderPosition, attack.direction, particles);
      const remainingDistance = Vec2.sub(targetPosition, attack.position).length();
      const shouldImpact =
        !attack.hasHit &&
        (remainingDistance <= attack.variant.projectileRadius || attack.elapsed >= attack.duration);
      if (shouldImpact) {
        attack.variant.impactEmitter(renderPosition, particles);
        this.screenShake = Math.min(1, this.screenShake + attack.variant.screenShake);
        if (this.enemy.isAlive()) {
          const facing = attack.direction.x >= 0 ? (1 as 1 | -1) : (-1 as 1 | -1);
          const damage = this.player.stats.strength * attack.variant.damageMultiplier;
          this.enemy.takeDamage(damage, attack.variant.knockback, facing);
          this.addScore(damage);
          this.services.audio.playHit('heavy');
          this.spawnDopaminePopup(
            this.enemy.position.x + this.enemy.width / 2,
            this.enemy.position.y - 30,
            'SUPER HIT!',
            '#ffe07c',
            1.4,
          );
          this.hitStreak = 0;
        }
        attack.hasHit = true;
        return false;
      }
      return true;
    });
  }

  private renderSuperAttacks(ctx: CanvasRenderingContext2D): void {
    this.activeSuperAttacks.forEach((attack) => {
      const { position, variant } = attack;
      const radius = variant.projectileRadius;
      const gradient = ctx.createRadialGradient(position.x, position.y, 0, position.x, position.y, radius);
      gradient.addColorStop(0, variant.coreColor);
      gradient.addColorStop(0.55, variant.coreColor);
      gradient.addColorStop(1, variant.auraColor);
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(position.x, position.y, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    });
  }

  private handleEnemyAI(dt: number): void {
    const distance = this.player.position.x - this.enemy.position.x;
    const direction = Math.sign(distance);
    const absDistance = Math.abs(distance);
    const verticalGap = this.player.position.y - this.enemy.position.y;

    this.enemy.faceTarget(this.player.position.x);

    if (absDistance > 48) {
      const intensity = absDistance > 80 ? 1.6 : 1.2;
      this.enemy.move(direction, dt * intensity);
    } else if (absDistance < 22) {
      this.enemy.move(-direction, dt * 1.1);
    } else {
      this.enemy.move(direction, dt * 1.05);
    }

    if (verticalGap < -12 && Math.random() < 0.02) {
      this.enemy.jump();
    }

    this.enemyAttackCooldown = Math.max(0, this.enemyAttackCooldown - dt);

    if (this.enemyAttackCooldown <= 0 && absDistance < 48) {
      const shouldKick = absDistance < 30 && Math.random() < 0.6;
      const attackType = shouldKick ? 'kick' : 'punch';
      this.enemy.tryAttack(attackType);
      this.enemyAttackCooldown = shouldKick ? 0.7 : 0.42;
    }

    if (absDistance > 36 && Math.random() < 0.012) {
      this.enemy.jump();
    }
  }

  private resolveCombat(context: EngineContext): void {
    if (this.player.canDealDamage()) {
      const attack = this.player.currentAttack();
      if (attack && this.collides(this.player, this.enemy, attack.type)) {
        this.enemy.takeDamage(attack.damage, attack.knockback, this.player.facing);
        this.addScore(attack.damage);
        this.spawnImpactParticles(
          context,
          this.enemy.position.x + this.enemy.width / 2,
          this.enemy.position.y + this.enemy.height / 2,
          attack.type === 'kick',
        );
        this.player.resolveHit();
        this.services.audio.playHit(attack.type === 'kick' ? 'heavy' : 'light');
        this.screenShake = Math.min(0.45, this.screenShake + (attack.type === 'kick' ? 0.22 : 0.15));
        this.hitStreak += 1;
        const desiredX =
          this.player.position.x +
          this.player.width / 2 +
          ARENA.width * 0.2 * (Math.random() < 0.5 ? -1 : 1);
        const messageX = Math.max(16, Math.min(ARENA.width - 16, desiredX));
        this.spawnDopaminePopup(
          messageX,
          this.player.position.y - 12,
          this.chooseHitMessage(),
          this.chooseHitColor(),
          0.7,
        );
      }
    }

    if (this.enemy.canDealDamage()) {
      const attack = this.enemy.currentAttack();
      if (attack && this.collides(this.enemy, this.player, attack.type)) {
        this.player.takeDamage(attack.damage, attack.knockback, this.enemy.facing);
        this.spawnImpactParticles(
          context,
          this.player.position.x + this.player.width / 2,
          this.player.position.y + this.player.height / 2,
          attack.type === 'kick',
        );
        this.enemy.resolveHit();
        this.services.audio.playHit(attack.type === 'kick' ? 'heavy' : 'light');
        this.screenShake = Math.min(0.45, this.screenShake + (attack.type === 'kick' ? 0.18 : 0.12));
        this.hitStreak = 0;
      }
    }
  }

  private collides(attacker: Fighter, defender: Fighter, type: 'punch' | 'kick'): boolean {
    const reach = type === 'kick' ? 24 : 18;
    const bounds = attacker.getBounds();
    const target = defender.getBounds();
    const left = attacker.facing > 0 ? bounds.right : bounds.left - reach;
    const right = attacker.facing > 0 ? bounds.right + reach : bounds.left;
    const top = bounds.top + 6;
    const bottom = bounds.bottom - 6;

    return !(right < target.left || left > target.right || bottom < target.top || top > target.bottom);
  }

  private spawnImpactParticles(
    context: EngineContext,
    x: number,
    y: number,
    heavy: boolean,
  ): void {
    const { particles } = context;
    const colors = heavy
      ? ['#ffef5a', '#ff6b6b', '#ffdede', '#9dffce']
      : ['#f7e26b', '#ff5f45', '#ffd1dc', '#8cffc1'];
    const count = heavy ? GAME_CONFIG.particleBurst + 10 : GAME_CONFIG.particleBurst;
    for (let i = 0; i < count; i += 1) {
      const speedMultiplier = heavy ? 220 : 150;
      particles.emit({
        position: new Vec2(x + (Math.random() - 0.5) * 20, y + (Math.random() - 0.5) * 14 - 6),
        velocity: new Vec2((Math.random() - 0.5) * speedMultiplier, -Math.random() * speedMultiplier * 0.6 - 40),
        lifetime: 0.35 + Math.random() * 0.35,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: heavy ? 4 : 3,
      });
    }
  }

  private checkOutcome(context: EngineContext): void {
    if (!this.enemy.isAlive()) {
      this.freezeSuperMeter();
      this.addScore(200 + this.level * 50);
      this.state = 'victory';
      this.stateTimer = 0.5;
      this.services.audio.playVictory();
      this.dopaminePopups = [];
      this.spawnDopaminePopup(
        this.player.position.x + this.player.width / 2,
        this.player.position.y - 24,
        `LEVEL ${this.level + 1} HYPE!`,
        '#ffef8a',
      );
    }
    if (!this.player.isAlive()) {
      this.freezeSuperMeter();
      this.state = 'defeat';
      this.stateTimer = 1;
      this.services.audio.playDefeat();
      this.services.audio.stopBackground();
      this.dopaminePopups = [];
      this.queueScoreSubmission();
      this.defeatBoardTimer = 0;
      this.defeatBoardVisible = false;
    }
  }

  private queueScoreSubmission(): void {
    if (this.scoreSubmissionStarted) {
      return;
    }
    this.scoreSubmissionStarted = true;
    this.scoreSubmissionMessage = 'Saving score...';
    const name = this.services.ranking.getNickname();
    this.playerNickname = name;
    this.services.ranking
      .submitScore(this.score, name)
      .then((snapshot) => {
        this.rankingEntries = snapshot.entries;
        this.playerPlacement = snapshot.placement;
        this.scoreSubmissionMessage =
          snapshot.placement > 0 ? `Recorded as #${snapshot.placement}` : 'Score saved';
      })
      .catch(() => {
        this.rankingEntries = this.services.ranking.getEntries();
        this.scoreSubmissionMessage = 'Saved offline';
      });
  }

  private configureLevel(): void {
    if (this.level === 1) {
      this.resetScaling();
      this.nextReward = this.createReward();
    } else {
      this.applyReward(this.nextReward);
      this.nextReward = this.createReward();
    }

    this.enemyStats = {
      maxHealth: ENEMY_BASE_STATS.maxHealth * this.enemyScaling.health,
      speed: GAME_CONFIG.baseEnemySpeed * this.enemyScaling.speed,
      strength: ENEMY_BASE_STATS.strength * this.enemyScaling.damage,
    };

    this.playerStats = {
      maxHealth: PLAYER_BASE_STATS.maxHealth * this.playerScaling.health,
      speed: PLAYER_BASE_STATS.speed * this.playerScaling.speed,
      strength: PLAYER_BASE_STATS.strength * this.playerScaling.damage,
    };

    this.enemy.stats = { ...this.enemyStats };
    this.player.stats = { ...this.playerStats };
  }

  private drawGround(ctx: CanvasRenderingContext2D): void {
    const palette = this.backdrop.palette();
    const gradient = ctx.createLinearGradient(0, ARENA.groundY - 6, 0, ARENA.groundY + 4);
    gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
    gradient.addColorStop(1, palette.glow);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, ARENA.groundY - 6, ARENA.width, 10);
  }

  private drawFighters(ctx: CanvasRenderingContext2D, time: number): void {
    this.drawFighter(ctx, this.player, time, true);
    this.drawFighter(ctx, this.enemy, time, false);
  }

  private drawFighter(ctx: CanvasRenderingContext2D, fighter: Fighter, time: number, isPlayer: boolean): void {
    const speed = fighter.horizontalSpeed();
    const { action, direction } = this.resolvePose(fighter, speed, isPlayer);
    const spriteSet = isPlayer
      ? this.services.sprites.player
      : this.services.sprites.enemy;
    const sprite = spriteSet[direction][action];
    if (!sprite) {
      return;
    }

    const bobFrequency = speed > 25 ? 18 : 6;
    const bobAmplitude = speed > 25 ? 2.6 : 1.4;
    const bobOffset = Math.sin(time * bobFrequency + (isPlayer ? 0 : Math.PI / 3)) * bobAmplitude;

    const anchorX = fighter.position.x + fighter.width / 2;
    const anchorY = fighter.position.y + fighter.height + bobOffset;

    const lean = fighter.isAttacking()
      ? 0.24 * fighter.facing
      : Math.max(-0.35, Math.min(0.35, (fighter.velocity.x / Math.max(60, fighter.stats.speed)) * 0.75));
    const stretchX = fighter.isAttacking()
      ? 1.18
      : 1 + Math.min(speed / 200, 0.32);
    const stretchY = fighter.isAttacking()
      ? 0.88
      : 1 - Math.min(speed / 260, 0.2);
    const baseScale = 0.32;

    const drawLayer = (
      alpha: number,
      offsetX: number,
      offsetY: number,
      withShadow: boolean,
      scaleBoost = 1,
    ): void => {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(anchorX + offsetX, anchorY + offsetY);
      ctx.rotate(lean);
      ctx.scale(baseScale * stretchX * scaleBoost, baseScale * stretchY * scaleBoost);
      if (withShadow) {
        ctx.shadowColor = isPlayer ? '#46ffd5' : '#ff6fa6';
        ctx.shadowBlur = 14 + speed * 0.08;
      } else {
        ctx.shadowBlur = 0;
      }
      const sourceWidth = sprite.width;
      const sourceHeight = sprite.height;
      ctx.drawImage(sprite, -sourceWidth / 2, -sourceHeight);
      ctx.restore();
    };

    if (speed > 55) {
      const layers = Math.min(3, Math.floor(speed / 45));
      for (let i = layers; i >= 1; i -= 1) {
        const offsetX = -fighter.facing * i * 6;
        const offsetY = i * 2.2;
        drawLayer(0.1 * i, offsetX, offsetY, false, 0.95);
      }
    }

    if (action === 'heavy') {
      drawLayer(0.3, 0, -8, false, 1.05);
    }

    drawLayer(1, 0, 0, true);
  }

  private resolvePose(fighter: Fighter, speed: number, isPlayer: boolean): {
    action: WombatAction;
    direction: WombatDirection;
  } {
    let action: WombatAction = 'idle';
    if (fighter.isAttacking()) {
      const currentType = fighter.attackType();
      action = currentType === 'kick' ? 'heavy' : 'jab';
    } else if (speed > fighter.stats.speed * 0.7) {
      action = 'jab';
    }

    let direction: WombatDirection;
    if (action === 'idle' && speed < 12 && isPlayer) {
      direction = 'front';
    } else {
      direction = fighter.facing < 0 ? 'left' : 'right';
    }

    return { action, direction };
  }

  private drawSuperMeter(ctx: CanvasRenderingContext2D): void {
    const barX = 20;
    const barY = ARENA.height - 20;
    const barWidth = 200;
    const barHeight = 10;
    const fillRatio = Math.max(0, Math.min(1, this.superMeter));
    ctx.save();
    ctx.translate(barX, barY);
    ctx.fillStyle = 'rgba(8, 24, 18, 0.85)';
    ctx.fillRect(0, 0, barWidth, barHeight);
    ctx.fillStyle = fillRatio >= 1 ? '#ffeac5' : '#ffaf62';
    ctx.fillRect(0, 0, barWidth * fillRatio, barHeight);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.18)';
    ctx.fillRect(0, 0, barWidth * fillRatio, barHeight / 2);
    ctx.strokeStyle = '#1f4a3b';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, barWidth - 1, barHeight - 1);
    ctx.font = UI.font;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'bottom';
    if (fillRatio >= 1) {
      const flashing = this.superReadyTimer > 0;
      const elapsed = this.superReadyFlashDuration - this.superReadyTimer;
      const scale = flashing ? 1 + Math.sin(elapsed * Math.PI * 3) * 0.16 : 1;
      ctx.save();
      ctx.translate(0, -4);
      ctx.scale(scale, scale);
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.55)';
      ctx.strokeText('SUPER READY - SPACE', 0, 0);
      ctx.fillStyle = '#ffeac5';
      ctx.fillText('SUPER READY - SPACE', 0, 0);
      ctx.restore();
    } else {
      const remaining = Math.max(0, this.superCooldownRemaining);
      ctx.fillStyle = '#ffe0a4';
      ctx.fillText(`SUPER CHARGING ${remaining.toFixed(1)}s`, 0, -4);
    }
    ctx.restore();
  }

  private drawDefeatSummary(ctx: CanvasRenderingContext2D): void {
    if (!this.defeatBoardVisible) {
      return;
    }
    this.drawDefeatRankingBoard(ctx);
  }

  private drawDefeatRankingBoard(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fillRect(0, 0, ARENA.width, ARENA.height);

    const scrollWidth = 232;
    const scrollHeight = 176;
    const { x, y } = centerScroll(scrollWidth, scrollHeight);
    drawScroll(ctx, { x, y, width: scrollWidth, height: scrollHeight });

    ctx.textAlign = 'center';
    ctx.fillStyle = '#4a3015';
    ctx.font = '9px "Press Start 2P", monospace';
    ctx.fillText('Top 5 Wombats', ARENA.centerX, y + 24);

    ctx.font = '8px "Press Start 2P", monospace';
    ctx.fillStyle = '#2b180b';
    ctx.fillText(`Score: ${this.score}`, ARENA.centerX, y + 42);
    const placementLabel = this.playerPlacement
      ? `Placement: #${this.playerPlacement}`
      : this.scoreSubmissionMessage || 'Saving score...';
    ctx.fillText(placementLabel, ARENA.centerX, y + 54);

    const entries = this.getTopRankingEntries(5);
    const listStartY = y + 74;
    const lineSpacing = 14;
    if (!entries.length) {
      ctx.fillStyle = UI.colorPrimary;
      ctx.fillText('No runs recorded yet.', ARENA.centerX, listStartY + 6);
      ctx.fillText('Be the first burrow legend!', ARENA.centerX, listStartY + 20);
    } else {
      ctx.textAlign = 'left';
      entries.forEach((entry, index) => {
        const rank = index + 1;
        const labelY = listStartY + index * lineSpacing;
        const highlight = this.playerPlacement === rank ? UI.colorPrimary : '#2b180b';
        ctx.fillStyle = highlight;
        ctx.fillText(`${rank}. ${entry.name}`, x + 30, labelY);
        ctx.textAlign = 'right';
        ctx.fillText(`${entry.score} pts`, x + scrollWidth - 30, labelY);
        ctx.textAlign = 'left';
      });
    }

    ctx.textAlign = 'center';
    ctx.font = '7px "Press Start 2P", monospace';
    ctx.fillStyle = UI.colorPrimary;
    ctx.fillText('Press confirm to return', ARENA.centerX, y + scrollHeight - 18);
    ctx.restore();
  }

  private getTopRankingEntries(limit: number): RankingEntry[] {
    const source = this.rankingEntries.length
      ? this.rankingEntries
      : this.services.ranking.getEntries();
    return source.slice(0, limit);
  }

  private scheduleIntro(): void {
    this.state = 'intro';
    this.stateTimer = 4;
    this.message = `${this.describeEnvironment(this.environmentType)} ${this.level}`;
  }

  private drawFooter(ctx: CanvasRenderingContext2D): void {
    const text = 'Made by Maqapaqa 2025 for LittleJS Game Jam 2025';
    ctx.save();
    ctx.font = '6px "Press Start 2P", monospace';
    ctx.textAlign = 'center';
    const y = ARENA.height - 4;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
    ctx.fillText(text, ARENA.centerX, y + 1);
    ctx.fillStyle = 'rgba(255, 246, 210, 0.9)';
    ctx.fillText(text, ARENA.centerX, y);
    ctx.restore();
  }

  private drawFactCard(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fillRect(0, 0, ARENA.width, ARENA.height);

    const scrollWidth = 212;
    const bodyFont = '9px "Press Start 2P", monospace';
    const lineSpacing = 15;
    ctx.font = bodyFont;
    const bodyLines = this.wrapFactText(ctx, this.currentFact, scrollWidth - 36);
    const contentHeight = bodyLines.length * lineSpacing;
    const baseHeight = 58 + contentHeight;
    const scrollHeight = Math.max(88, baseHeight * 0.95);
    const { x, y } = centerScroll(scrollWidth, scrollHeight);
    const adjustedY = Math.max(12, y - scrollHeight * 0.05);

    drawScroll(ctx, { x, y: adjustedY, width: scrollWidth, height: scrollHeight });

    ctx.fillStyle = '#4a3015';
    ctx.textAlign = 'center';
    ctx.font = '9px "Press Start 2P", monospace';
    ctx.fillText('Wombat Fact', ARENA.centerX, adjustedY + 26);

    ctx.font = bodyFont;
    let cursorY = adjustedY + 44;
    bodyLines.forEach((line) => {
      ctx.fillText(line, ARENA.centerX, cursorY);
      cursorY += lineSpacing;
    });

    this.factCardBounds = { x, y: adjustedY, width: scrollWidth, height: scrollHeight };
    ctx.restore();
  }

  private wrapFactText(
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
  ): string[] {
    const words = text.split(/\s+/);
    const lines: string[] = [];
    let currentLine = '';
    words.forEach((word) => {
      const candidate = currentLine ? `${currentLine} ${word}` : word;
      if (ctx.measureText(candidate).width <= maxWidth || !currentLine) {
        currentLine = candidate;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    });
    if (currentLine) {
      lines.push(currentLine);
    }
    return lines;
  }

  private drawHUD(ctx: CanvasRenderingContext2D): void {
    this.drawHealthBar(ctx, 20, 16, this.player.state.health, this.player.stats.maxHealth, '#6bffce');
    drawText(ctx, `Player ${this.getShortNickname()}`, 20, 12, 'left', UI.font, '#afffe4');
    this.drawHealthBar(
      ctx,
      ARENA.width - 140,
      16,
      this.enemy.state.health,
      this.enemy.stats.maxHealth,
      '#ff7da7',
    );
    drawText(ctx, `Enemy ${this.enemyName} (L${this.level})`, ARENA.width - 196, 12, 'left', UI.font, '#ffd7e6');
    const rewardSpeed = Math.round(this.nextReward.playerSpeed * 100);
    const rewardDamage = Math.round(this.nextReward.playerDamage * 100);
    drawText(
      ctx,
      `Next reward: +${rewardSpeed}% SPD / +${rewardDamage}% DMG`,
      ARENA.width - 176,
      38,
      'left',
      UI.font,
      '#ffe29a',
    );
    drawCenteredText(ctx, `Score ${this.score}`, 32, UI.font, '#ffeac5');
  }

  private drawHealthBar(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    value: number,
    max: number,
    color: string,
  ): void {
    const width = 120;
    const ratio = Math.max(0, value / max);
    ctx.fillStyle = 'rgba(8, 24, 18, 0.92)';
    ctx.fillRect(x, y, width, 8);
    ctx.fillStyle = color;
    ctx.fillRect(x, y, width * ratio, 8);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
    ctx.fillRect(x, y, width * ratio, 3);
    ctx.strokeStyle = '#1f4a3b';
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 0.5, y + 0.5, width - 1, 7);
  }

  private confirmPressed(context: EngineContext): boolean {
    return context.input.isPressed('enter') || context.input.isPressed(' ');
  }

  private environmentForLevel(level: number): EnvironmentType {
    if (level < 3) {
      return 'forest';
    }
    if (level < 7) {
      return 'desert';
    }
    if (level < 10) {
      return 'glacier';
    }

    const segments: Array<{ length: number; env: EnvironmentType }> = [
      { length: 7, env: 'forest' },
      { length: 3, env: 'desert' },
      { length: 3, env: 'glacier' },
    ];

    let cursor = 10;
    let index = 0;
    while (true) {
      const segment = segments[index % segments.length];
      if (level < cursor + segment.length) {
        return segment.env;
      }
      cursor += segment.length;
      index += 1;
    }
  }

  private describeEnvironment(env: EnvironmentType): string {
    switch (env) {
      case 'desert':
        return 'Desert';
      case 'glacier':
        return 'Glacier';
      default:
        return 'Forest';
    }
  }

  private getShortNickname(): string {
    if (this.playerNickname.length <= 14) {
      return this.playerNickname;
    }
    return `${this.playerNickname.slice(0, 11)}...`;
  }

  private victoryPalette(): { headline: string; body: string } {
    switch (this.environmentType) {
      case 'desert':
        return { headline: '#fff0c7', body: '#ffb85c' };
      case 'glacier':
        return { headline: '#e0f8ff', body: '#89d9ff' };
      default:
        return { headline: '#e1ff7a', body: '#7afcdf' };
    }
  }

  private createReward(): LevelReward {
    const roll = (): number => 0.05 + Math.random() * 0.05;
    const rollHealth = (): number => 0.03 + Math.random() * 0.03;
    return {
      playerSpeed: roll(),
      playerDamage: roll(),
      playerHealth: rollHealth(),
      enemySpeed: roll(),
      enemyDamage: roll(),
      enemyHealth: rollHealth() + 0.01,
    };
  }

  private resetScaling(): void {
    this.playerScaling = { speed: 1, damage: 1, health: 1 };
    this.enemyScaling = { speed: 1, damage: 1, health: 1 };
  }

  private beginFight(): void {
    this.activeSuperAttacks = [];
    if (this.superMeter >= 1) {
      this.superReadyAnnounced = true;
      this.superReadyTimer = this.superReadyFlashDuration;
    } else {
      this.superReadyAnnounced = false;
      this.superReadyTimer = 0;
    }
  }

  private freezeSuperMeter(): void {
    this.superCooldownRemaining = clamp(this.superCooldown * (1 - this.superMeter), 0, this.superCooldown);
    this.superReadyTimer = 0;
    this.activeSuperAttacks = [];
  }

  private applyReward(reward: LevelReward): void {
    this.playerScaling = {
      speed: this.playerScaling.speed * (1 + reward.playerSpeed),
      damage: this.playerScaling.damage * (1 + reward.playerDamage),
      health: this.playerScaling.health * (1 + reward.playerHealth),
    };
    this.enemyScaling = {
      speed: this.enemyScaling.speed * (1 + reward.enemySpeed),
      damage: this.enemyScaling.damage * (1 + reward.enemyDamage),
      health: this.enemyScaling.health * (1 + reward.enemyHealth),
    };
  }

  private spawnDopaminePopup(
    x: number,
    y: number,
    text: string,
    color: string,
    duration = 1.2,
  ): void {
    this.dopaminePopups.push({ text, timer: duration, duration, x, y, color });
  }

  private updatePopups(dt: number): void {
    this.dopaminePopups = this.dopaminePopups
      .map((popup) => ({ ...popup, timer: popup.timer - dt, y: popup.y - dt * 24 }))
      .filter((popup) => popup.timer > 0);
  }

  private drawPopups(ctx: CanvasRenderingContext2D): void {
    this.dopaminePopups.forEach((popup) => {
      const alpha = Math.max(0, popup.timer / popup.duration);
      ctx.save();
      ctx.globalAlpha = alpha;
      drawText(ctx, popup.text, popup.x, popup.y, 'center', UI.font, popup.color);
      ctx.restore();
    });
  }

  private chooseHitMessage(): string {
    if (this.hitStreak >= 6) {
      return `STREAK x${this.hitStreak}`;
    }
    if (this.hitStreak >= 3) {
      return 'COMBO!';
    }
    const choices = ['NICE!', 'CRUNCH!', 'SMASH!', 'BOOST!'];
    return choices[Math.floor(Math.random() * choices.length)];
  }

  private chooseHitColor(): string {
    const palette = ['#ff8160', '#ffe066', '#ff9f1a', '#ff4f79'];
    return palette[Math.floor(Math.random() * palette.length)];
  }

  private drawKatanaMotion(
    ctx: CanvasRenderingContext2D,
    fighter: Fighter,
    action: WombatAction,
    time: number,
    isPlayer: boolean,
  ): void {
    if (!fighter.isAttacking()) {
      return;
    }
    const progress = fighter.attackProgress();
    const facing = fighter.facing;
    const anchorX = fighter.position.x + fighter.width / 2 + facing * 6;
    const anchorY = fighter.position.y + fighter.height - 16;
    const sweep = action === 'heavy' ? Math.PI * 1.1 : Math.PI * 0.78;
    const startAngle = facing > 0 ? -Math.PI * 0.35 : Math.PI + Math.PI * 0.35;
    const direction = facing > 0 ? 1 : -1;
    const angle = startAngle + sweep * progress * direction;
    const bladeLength = action === 'heavy' ? 46 : 36;
    const tipX = anchorX + Math.cos(angle) * bladeLength;
    const tipY = anchorY + Math.sin(angle) * bladeLength;

    const shimmer = 0.45 + 0.55 * Math.abs(Math.sin(time * 8 + progress * Math.PI));

    ctx.save();
    ctx.lineCap = 'round';
    ctx.strokeStyle = isPlayer
      ? `rgba(255, 247, 218, ${0.6 + 0.3 * shimmer})`
      : `rgba(228, 243, 255, ${0.6 + 0.3 * shimmer})`;
    ctx.lineWidth = action === 'heavy' ? 3.6 : 3;
    ctx.beginPath();
    ctx.moveTo(anchorX, anchorY);
    ctx.lineTo(tipX, tipY);
    ctx.stroke();

    ctx.strokeStyle = isPlayer
      ? `rgba(255, 242, 200, ${0.25 + 0.25 * shimmer})`
      : `rgba(208, 236, 255, ${0.25 + 0.25 * shimmer})`;
    ctx.lineWidth = 2;
    const trailAngle = angle - direction * 0.3;
    const trailLength = bladeLength * 0.78;
    ctx.beginPath();
    ctx.moveTo(anchorX, anchorY);
    ctx.lineTo(
      anchorX + Math.cos(trailAngle) * trailLength,
      anchorY + Math.sin(trailAngle) * trailLength,
    );
    ctx.stroke();

    ctx.strokeStyle = '#353535';
    ctx.lineWidth = 2;
    const guardAngle = angle + Math.PI / 2;
    const guardOffset = 6;
    ctx.beginPath();
    ctx.moveTo(
      anchorX + Math.cos(guardAngle) * guardOffset,
      anchorY + Math.sin(guardAngle) * guardOffset,
    );
    ctx.lineTo(
      anchorX - Math.cos(guardAngle) * guardOffset,
      anchorY - Math.sin(guardAngle) * guardOffset,
    );
    ctx.stroke();
    ctx.restore();
  }
}
